import BaseStore from './BaseStore';
import Trip from '../records/Trip';
import OverallCheapestSelection from '../records/OverallCheapestSelection';
import CheapestMatchingFares from '../records/trip/CheapestMatchingFares';
import Passengers from '../records/Passengers';
import {
    ActionTypes,
    JourneyDirections,
    JourneyPlannerLayout,
    FareFilterSelections
} from '../constants/AppConstants';
import Photocard from '../records/Photocard';

export class CurrentTripStore extends BaseStore {
    get() {
        return this.state;
    }

    deserialize(state) {
        return state ? Trip.fromJSON(state) : new Trip();
    }

    serialize() {
        return Object.assign(this.state.toJS(), {
            searchClass: this.state.search.constructor.name,
        });
    }

    handleInwardServiceSelected({service}) {
        this.setState(
            this.state
                .set('returnJourney', service)
                .set('raw', false)
                .setIn(['fares', 'returnSingle'], null),
        );
    }

    handleOutwardServiceSelected({service}) {
        this.setState(
            this.state
                .set('outwardJourney', service)
                .set('raw', false)
                .resetFares(),
        );
    }

    handleSelectFare({fare, direction, service, fareCategory, fareClass}) {
        this.setState(
            this.state
                .set('fareCategory', fareCategory)
                .set('fareClass', fareClass)
                .setFareForDirection(direction, fare)
                .setServiceForDirection(direction, service)
                .setSeatingForDirection(direction, fare, service)
                .set('raw', false),
        );
    }

    handleSelectingCheapestFare() {
        this.setState(
            this.state
                .set('selectingCheapestFare', true),
        );
    }

    handleSelectingNonCheapestFare() {
        this.setState(
            this.state
                .set('selectingCheapestFare', false),
        );
    }

    handleSelectSeasonTicket({fare}) {
        this.setState(this.state.setSeasonFare(fare));
    }

    handleSelectDaySaveTicket({fare}) {
        this.setState(this.state.setFareForDirection(JourneyDirections.OUTWARD, fare));
    }

    handleSelectRoversRangersTicket({fare}) {
        const passengers = Passengers.fromTickets(fare.ticketType, fare.tickets);
        this.setState(
            this.state
                .setFareForDirection(JourneyDirections.OUTWARD, fare)
                .set('passengers', passengers),
        );
    }

    handlePassengersUpdate({pax}) {
        this.setState(this.state.set('passengers', this.state.passengers.set('pax', pax)));
    }

    handlePhotocardUpdate({photocard}) {
        this.setState(this.state.set('photocard', photocard));
    }

    handlePhotocardFieldUpdate({field, value}) {
        this.setState(this.state.setIn(['photocard', field], value));
    }

    handleClearPhotocard() {
        this.setState(this.state.set('photocard', new Photocard()));
    }

    handleSeasonSearchFinished({seasonTickets}) {
        if (!seasonTickets.includes(this.state.getSeasonFare())) {
            let selectedFare;
            const fare = seasonTickets.first();

            if (fare) {
                selectedFare = fare;
            }

            this.handleSelectSeasonTicket({fare: selectedFare});
        }
    }

    handleOtherSearchFinished({searchResults}) {
        const fare = searchResults.first();
        if (fare) {
            const passengers = Passengers.fromTickets(fare.ticketType, fare.tickets);
            this.setState(
                this.state
                    .setFareForDirection(JourneyDirections.OUTWARD, fare)
                    .set('passengers', passengers),
            );
        }
    }

    handleSmartCarnetFinshed({fares}) {
        const fare = fares.first();
        const passengers = Passengers.fromTickets(fare.ticketType, fare.tickets);
        this.setState(this.state.set('passengers', passengers));
    }

    handleSelectSmartCarnetTicket({fare}) {
        if (fare) {
            const passengers = Passengers.fromTickets(fare.ticketType, fare.tickets);

            this.setState(
                this.state
                    .setFareForDirection(JourneyDirections.OUTWARD, fare)
                    .set('passengers', passengers),
            );
        } else {
            this.setState(
                this.state.setFareForDirection(JourneyDirections.OUTWARD, fare)
            );
        }
    }

    handleSelectSmartCarnetTicketReturn({fare}) {
        this.setState(
            this.state.setFareForDirection(JourneyDirections.INWARD, fare)
        );
    }

    handleOrderTripCreated({trip}) {
        this.setState(trip);
    }

    handleSearchStarted({search}) {
        this.setState(new Trip({search}));
    }

    handleSearchUpdated({search}) {
        this.setState(this.state.set('search', search));
    }

    handleResultsUpdated({result, tripDirection}) {

        const isDisabledForTrip = service => !service.isDisabledForTrip(this.state);

        this.setState(
            this.state
                .set(
                    'cheapest',
                    OverallCheapestSelection.fromServices(
                        result.outward,
                        result.inward,
                        this.state,
                    ),
                )
                .set(
                    'cheapestFastest',
                    CheapestMatchingFares.fromServices(
                        ...this.getFastestServices(result.outward.filter(isDisabledForTrip), result.inward.filter(isDisabledForTrip)),
                    ).getOverallCheapest(),
                )
                .set(
                    'cheapestFirstClass',
                    CheapestMatchingFares.fromServices(result.outward.filter(isDisabledForTrip), result.inward.filter(isDisabledForTrip), fare =>
                        fare.isFirstClass(),
                    ).getOverallCheapest(),
                )
                .set('hasServiceProvider', result.hasServiceProvider)
                .resetSelected(tripDirection),
        );

        if (this.state.search.targetLayout === JourneyPlannerLayout.GRID || this.state.search.targetLayout === JourneyPlannerLayout.GRID_2) {
            this.handlePreselectCheapest();
        }
    }

    handleSearchFinished({outward, inward, hasServiceProvider}) {
        const isDisabledForTrip = service => !service.isDisabledForTrip(this.state);

        this.setState(
            this.state
                .set('cheapest', OverallCheapestSelection.fromServices(outward, inward, this.state))
                .set(
                    'cheapestFastest',
                    CheapestMatchingFares.fromServices(
                        ...this.getFastestServices(outward.filter(isDisabledForTrip), inward.filter(isDisabledForTrip)),
                    ).getOverallCheapest(),
                )
                .set(
                    'cheapestFirstClass',
                    CheapestMatchingFares.fromServices(outward.filter(isDisabledForTrip), inward.filter(isDisabledForTrip), fare =>
                        fare.isFirstClass(),
                    ).getOverallCheapest(),
                )
                .set('hasServiceProvider', hasServiceProvider),
        );

        if (this.state.search.targetLayout === JourneyPlannerLayout.GRID || this.state.search.targetLayout === JourneyPlannerLayout.GRID_2) {
            this.handlePreselectCheapest();
        }
    }

    getFastestServices(outward, inward) {
        const getServiceDuration = service => service.journey.getTripDuration();
        const getFastest = services =>
            services
                .sortBy(getServiceDuration)
                .groupBy(getServiceDuration)
                .first();
        return [getFastest(outward), inward && getFastest(inward)];
    }

    handleClear () {
        this.setState(this.state.resetSelected());
    }

    handlePlusBusLoaded ({ plusBus }) {
        this.setState(this.state.set('plusBusAvailability', plusBus));
    }

    handleSelectFareCategory ({ fareCategory, service, fareClass }) {
        this.setState(this.state
            .set('fareCategory', fareCategory)
            .set('fareClass', fareClass)
            .set('outwardJourney', service)
            .set('returnJourney', null)
            .resetFares()
        );
    }

    handleUpdateDisplayFilters({filters}) {
        let newState = this.state.set('filters', filters);
        if (filters.selection !== this.state.filters.selection) {
            newState = this.getPreselectedState(newState);
        }
        this.setState(newState);
    }

    handlePreselectCheapest() {
        this.setState(this.getPreselectedState());
    }

    getPreselectedState(state = this.state) {
        const {selection} = state.filters || FareFilterSelections.CHEAPEST;
        const cheapestSelection =
            selection === FareFilterSelections.FASTEST
                ? state.cheapestFastest
                : selection === FareFilterSelections.FIRST_CLASS
                ? state.cheapestFirstClass
                : state.cheapest;
        const fareCategory = state.getCheapestFareCategory(cheapestSelection);
        const fareClass = state.getCheapestFareClass(cheapestSelection);

        let newState = state
            .set('fareCategory', fareCategory)
            .set('fareClass', fareClass)
            .set('outwardJourney', cheapestSelection.outwardService)
            .set('returnJourney', cheapestSelection.inboundService);

        if (cheapestSelection.hasOutwardFare()) {
            newState = newState
                .setFareForDirection(JourneyDirections.OUTWARD, cheapestSelection.getOutwardFare())
                .setSeatingForDirection(
                    JourneyDirections.OUTWARD,
                    cheapestSelection.getOutwardFare(),
                    cheapestSelection.outwardService,
                );
        }

        if (cheapestSelection.hasInwardFare()) {
            newState = newState
                .setFareForDirection(JourneyDirections.INWARD, cheapestSelection.getInwardFare())
                .setSeatingForDirection(
                    JourneyDirections.INWARD,
                    cheapestSelection.getInwardFare(),
                    cheapestSelection.inboundService,
                );
        }

        if (cheapestSelection.hasReturnFare()) {
            newState = newState.setFareForDirection(
                JourneyDirections.OUTWARD,
                cheapestSelection.getReturnFare(),
            );
        }

        return newState;
    }

    handleSelectFareOnly ({ fare }) {
        this.setState(this.state.setFareOnlyFare(fare));
    }

    handleSetRequestBikeReservation ({ bikes }) {
        return this.setState(this.state.set('bikes', bikes));
    }

    getActionHandlers () {
        return {
            [ActionTypes.INWARD_SERVICE_SELECTED]: this.handleInwardServiceSelected,
            [ActionTypes.OUTWARD_SERVICE_SELECTED]: this.handleOutwardServiceSelected,
            [ActionTypes.FARE_SELECTED]: this.handleSelectFare,
            [ActionTypes.FARE_ONLY_SELECTED]: this.handleSelectFareOnly,
            [ActionTypes.SELECTING_CHEAPEST_FARE]: this.handleSelectingCheapestFare,
            [ActionTypes.SELECTING_NON_CHEAPEST_FARE]: this.handleSelectingNonCheapestFare,
            [ActionTypes.TRIP_UPDATED]: this.handleOrderTripCreated,
            [ActionTypes.ORDER_PLUSBUS_UPDATED]: this.handleOrderTripCreated,

            [ActionTypes.SEASON_TICKET_SELECTED]: this.handleSelectSeasonTicket,
            [ActionTypes.SEASON_TICKET_PHOTOCARD_UPDATED]: this.handlePhotocardUpdate,
            [ActionTypes.SEASON_TICKET_PHOTOCARD_FIELD_UPDATED]: this.handlePhotocardFieldUpdate,
            [ActionTypes.SEASON_TICKET_PHOTOCARD_CLEAR]: this.handleClearPhotocard,

            [ActionTypes.ORDER_TRIP_CREATED]: this.handleOrderTripCreated,
            [ActionTypes.ORDER_CREATED]: this.handleOrderTripCreated,

            [ActionTypes.PRESELECT_CHEAPEST]: this.handlePreselectCheapest,
            [ActionTypes.UPDATE_DISPLAY_FILTERS]: this.handleUpdateDisplayFilters,

            [ActionTypes.SEARCH_FAILED]: this.handleSearchStarted,
            [ActionTypes.SEARCH_STARTED]: this.handleSearchStarted,
            [ActionTypes.SEARCH_LOADED]: this.handleSearchUpdated,
            [ActionTypes.SEARCH_UPDATED]: this.handleSearchUpdated,
            [ActionTypes.SEARCH_FINISHED]: this.handleSearchFinished,

            [ActionTypes.FARE_ONLY_SEARCH_STARTED]: this.handleSearchStarted,
            [ActionTypes.FARE_ONLY_SEASON_SEARCH_STARTED]: this.handleSearchStarted,

            [ActionTypes.SEASON_SEARCH_LOADED]: this.handleSearchUpdated,
            [ActionTypes.SEASON_SEARCH_STARTED]: this.handleSearchStarted,
            [ActionTypes.SEASON_SEARCH_FINISHED]: this.handleSeasonSearchFinished,

            [ActionTypes.DAYSAVE_SEARCH_STARTED]: this.handleSearchStarted,
            [ActionTypes.DAYSAVE_SEARCH_FINISHED]: this.handleOtherSearchFinished,
            [ActionTypes.DAYSAVE_TICKET_SELECTED]: this.handleSelectDaySaveTicket,

            [ActionTypes.ROVERSRANGERS_SEARCH_STARTED]: this.handleSearchStarted,
            [ActionTypes.ROVERSRANGERS_SEARCH_FINISHED]: this.handleOtherSearchFinished,
            [ActionTypes.ROVERSRANGERS_SEARCH_LOADED]: this.handleSearchUpdated,
            [ActionTypes.ROVERSRANGERS_TICKET_PASSENGERS_CHANGED]: this.handlePassengersUpdate,
            [ActionTypes.ROVERSRANGERS_TICKET_SELECTED]: this.handleSelectRoversRangersTicket,

            [ActionTypes.SMART_CARNET_SEARCH_STARTED]: this.handleSearchStarted,
            [ActionTypes.SMART_CARNET_SEARCH_FINISHED]: this.handleSmartCarnetFinshed,
            [ActionTypes.SMART_CARNET_SEARCH_SELECTED]: this.handleSelectSmartCarnetTicket,
            [ActionTypes.SMART_CARNET_RETURN_SEARCH_SELECTED]: this.handleSelectSmartCarnetTicketReturn,

            [ActionTypes.ORDER_PAID]: this.handleClear,
            [ActionTypes.LOGGED_OUT]: this.handleClear,
            [ActionTypes.TRIP_PLUSBUS_AVAILABILITY_LOADED]: this.handlePlusBusLoaded,

            [ ActionTypes.SHOW_EARLIER_TRAINS_STARTED ]: this.handleSearchUpdated,
            [ ActionTypes.SHOW_LATER_TRAINS_STARTED ]: this.handleSearchUpdated,
            [ ActionTypes.SHOW_EARLIER_TRAINS_FINISHED ]: this.handleResultsUpdated,
            [ ActionTypes.SHOW_LATER_TRAINS_FINISHED ]: this.handleResultsUpdated,

            [ ActionTypes.FARE_CATEGORY_SELECTED ]: this.handleSelectFareCategory,

            [ ActionTypes.SEASON_RESULTS_FILTERS_CHANGE ]: this.handleSeasonSearchFinished,

            [ ActionTypes.SET_TRIP_BIKES]: this.handleSetRequestBikeReservation
        };
    }


}

const instance = new CurrentTripStore({ storageKey: 'current-trip' });

export default instance;



// WEBPACK FOOTER //
// ./src/stores/CurrentTripStore.js